import { useMemo, useState } from 'react';
import { Popover } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

export interface MultiSelectOption {
	value: string;
	label: string;
}

interface MultiSelectFilterProps {
	label: string;
	options: MultiSelectOption[];
	values: string[];
	onChange: (values: string[]) => void;
	placeholder?: string;
}

function summarize(values: string[], options: MultiSelectOption[], placeholder: string): string {
	if (values.length === 0) return placeholder;
	if (values.length === 1) return options.find(o => o.value === values[0])?.label ?? values[0];
	return `${values.length} selecionados`;
}

export function MultiSelectFilter({
	label,
	options,
	values,
	onChange,
	placeholder = 'Todos',
}: MultiSelectFilterProps) {
	const [search, setSearch] = useState('');

	const visible = useMemo(() => {
		const term = search.trim().toLowerCase();
		return term ? options.filter(o => o.label.toLowerCase().includes(term)) : options;
	}, [options, search]);

	const toggle = (value: string) => {
		onChange(values.includes(value) ? values.filter(v => v !== value) : [...values, value]);
	};

	return (
		<Popover className="relative">
			<Popover.Button
				aria-label={label}
				className="flex w-full items-center justify-between gap-2 rounded-lg border border-raro-gray-200 bg-white px-3 py-2 text-left text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
			>
				<span className={clsx('truncate', values.length === 0 ? 'text-raro-gray-500' : 'text-raro-gray-900')}>
					{summarize(values, options, placeholder)}
				</span>
				<ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-raro-gray-400" />
			</Popover.Button>

			<Popover.Panel className="absolute left-0 z-20 mt-1 w-full min-w-full sm:min-w-[220px] rounded-lg border border-raro-gray-200 bg-white shadow-lg">
				<div className="border-b border-raro-gray-100 p-2">
					<input
						type="text"
						value={search}
						onChange={e => setSearch(e.target.value)}
						placeholder="Buscar..."
						aria-label={`Buscar em ${label}`}
						className="w-full rounded border border-raro-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
					/>
				</div>

				<ul className="max-h-60 overflow-y-auto py-1">
					{visible.length === 0 && (
						<li className="px-3 py-2 text-sm text-raro-gray-500">Nenhuma opção encontrada</li>
					)}
					{visible.map(option => {
						const checked = values.includes(option.value);
						return (
							<li key={option.value}>
								<button
									type="button"
									role="checkbox"
									aria-checked={checked}
									onClick={() => toggle(option.value)}
									className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-raro-gray-800 hover:bg-raro-gray-50"
								>
									<span
										className={clsx(
											'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
											checked ? 'border-primary-600 bg-primary-600' : 'border-raro-gray-300'
										)}
									>
										{checked && <CheckIcon className="h-3 w-3 text-white" />}
									</span>
									<span className="truncate">{option.label}</span>
								</button>
							</li>
						);
					})}
				</ul>

				{values.length > 0 && (
					<div className="border-t border-raro-gray-100 p-2">
						<button
							type="button"
							onClick={() => onChange([])}
							className="w-full rounded px-2 py-1.5 text-sm text-raro-gray-600 hover:bg-raro-gray-50"
						>
							Limpar seleção
						</button>
					</div>
				)}
			</Popover.Panel>
		</Popover>
	);
}
